'use client'

import { z } from 'zod'

import { Combobox } from '@/core/components/form/combobox'
import { InputText } from '@/core/components/form/input-text'
import { Textarea } from '@/core/components/form/textarea'
import { importLineSchema } from '@/features/studio-renew/schemas/import-result.schema'

/** 원고의 고정된 대사 순서를 유지하면서 모든 화자·대사를 검토한다. */
export default function ImportDialogueEditor({
  value,
  disabled,
  onChange,
}: {
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  let raw: unknown
  try {
    raw = JSON.parse(value)
  } catch {
    raw = null
  }
  const parsed = z.array(importLineSchema).safeParse(raw)
  if (!parsed.success)
    return (
      <Textarea
        aria-label="시작 대화 원문"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        rows={5}
      />
    )
  const rows = parsed.data
  const update = (
    index: number,
    patch: Partial<z.infer<typeof importLineSchema>>
  ) =>
    onChange(
      JSON.stringify(
        rows.map((row, position) =>
          position === index ? { ...row, ...patch } : row
        )
      )
    )
  return (
    <div className="grid gap-4">
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid gap-2 border-t border-border pt-3 first:border-t-0 first:pt-0"
        >
          <Combobox
            ariaLabel={`시작 대화 ${index + 1} 화자 종류`}
            value={row.who}
            options={[
              { value: 'char', label: '캐릭터' },
              { value: 'user', label: '이용자' },
              { value: 'narration', label: '지문' },
            ]}
            disabled={disabled}
            onChange={(who) =>
              update(index, {
                who: z.enum(['char', 'user', 'narration']).parse(who),
              })
            }
          />
          {row.who === 'char' && (
            <InputText
              aria-label={`시작 대화 ${index + 1} 캐릭터 이름`}
              value={row.characterName ?? ''}
              disabled={disabled}
              onChange={(event) =>
                update(index, { characterName: event.target.value })
              }
            />
          )}
          <Textarea
            aria-label={`시작 대화 ${index + 1} 내용`}
            rows={3}
            value={row.text}
            disabled={disabled}
            onChange={(event) => update(index, { text: event.target.value })}
          />
        </div>
      ))}
    </div>
  )
}
